import React from 'react';
import Box from '@mui/material/Box';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme as useAppTheme } from '../../context/ThemeContext';

/**
 * Segmented light / dark control (UX pill); persists via ThemeProvider (hub360_color_mode).
 * Visual selection follows effective mode (incl. "auto"); click always sets explicit light/dark.
 */
export default function ThemeToggle({ variant = 'default' }) {
  const { t } = useTranslation();
  const mui = useMuiTheme();
  const { isDark, setPreference } = useAppTheme();
  const isTopbar = variant === 'topbar';

  const isLightSelected = !isDark;
  const isDarkSelected = isDark;

  const trackBg =
    mui.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : '#F0F2F5';
  const activeSurface =
    mui.palette.mode === 'dark' ? mui.palette.grey[800] : '#ffffff';
  const activeShadow =
    mui.palette.mode === 'dark'
      ? '0 1px 3px rgba(0, 0, 0, 0.45)'
      : '0 1px 4px rgba(15, 23, 42, 0.12)';

  const segmentStyle = (selected) => ({
    flex: isTopbar ? '0 0 auto' : 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: { xs: 0.5, sm: 0.75 },
    minWidth: isTopbar ? 'auto' : 0,
    minHeight: isTopbar ? 34 : undefined,
    py: isTopbar ? 0.5 : 0.75,
    px: { xs: 1, sm: 1.25 },
    border: 'none',
    borderRadius: 9999,
    cursor: 'pointer',
    fontFamily: mui.typography.fontFamily,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.2,
    color: selected ? mui.palette.text.primary : mui.palette.text.secondary,
    backgroundColor: selected ? activeSurface : 'transparent',
    boxShadow: selected ? activeShadow : 'none',
    transition: mui.transitions.create(
      ['background-color', 'color', 'box-shadow'],
      { duration: mui.transitions.duration.short }
    ),
    '&:focus-visible': {
      outline: `2px solid ${mui.palette.primary.main}`,
      outlineOffset: 2,
    },
  });

  return (
    <Box
      data-testid="theme-toggle"
      className={`theme-mode-toggle${isTopbar ? ' theme-mode-toggle--topbar' : ''}`}
      role="group"
      aria-label={t('common.themeAppearanceGroup')}
      sx={{
        display: 'inline-flex',
        alignItems: 'stretch',
        p: 0.5,
        borderRadius: 9999,
        backgroundColor: trackBg,
        maxWidth: '100%',
        gap: isTopbar ? 0.5 : 0,
      }}
    >
      <Box
        component="button"
        type="button"
        aria-pressed={isLightSelected}
        aria-label={t('common.themeModeLight')}
        onClick={() => setPreference('light')}
        sx={segmentStyle(isLightSelected)}
      >
        <LightModeIcon sx={{ fontSize: 18 }} aria-hidden />
        <Box
          component="span"
          sx={{
            display: isTopbar ? 'inline' : { xs: 'none', sm: 'inline' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {t('common.themeModeLight')}
        </Box>
      </Box>
      <Box
        component="button"
        type="button"
        aria-pressed={isDarkSelected}
        aria-label={t('common.themeModeDark')}
        onClick={() => setPreference('dark')}
        sx={segmentStyle(isDarkSelected)}
      >
        <DarkModeIcon sx={{ fontSize: 18 }} aria-hidden />
        <Box
          component="span"
          sx={{
            display: isTopbar ? 'inline' : { xs: 'none', sm: 'inline' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {t('common.themeModeDark')}
        </Box>
      </Box>
    </Box>
  );
}
